import i18n from 'i18next'

import { BaseInterface } from '../../BaseInterface'

export interface BaseDBOptions<T = any> {
  autoInit?: boolean
  key?: string
  dataKey?: string
  version?: number
  indexList?: string[]
  expireDuration?: number | null
  expireDurationForEmpty?: number | null
  skipPreload?: boolean
  getSaveData?: (item: any, key?: IDBValidKey) => T
  beforeRemoveSaveData?: (item: T) => void
  checkForList?: (item: T, key: IDBValidKey) => boolean
}

interface DBItem<T = any> {
  expireTime?: number
  isExpired?: () => boolean
  [key: string]: number | string | any | T
}

interface WaitingRequest {
  resolve: (value: any) => void
  reject: (error: any) => void
}

class BaseDB<T = any> implements BaseInterface<T> {
  dbName: string
  isActive: boolean
  db: IDBDatabase | null
  loaded: boolean
  autoInit: boolean
  key: string
  dataKey: string
  indexedDataKey: boolean
  version: number
  indexList: string[]
  expireDuration: number | null
  expireDurationForEmpty: number | null
  skipPreload: boolean
  dataMap: Record<string, T>
  isInitialing: boolean
  initWaitingReq: WaitingRequest[]
  isloading: boolean
  loadWaitingReq: WaitingRequest[]
  options: BaseDBOptions<T>
  versionOutdated: boolean

  constructor(name: string, options: BaseDBOptions<T>) {
    this.dbName = name
    this.isActive = false
    this.db = null
    this.loaded = false
    this.options = options

    this.autoInit = options.autoInit !== false

    this.key = options.key || 'key'
    this.dataKey = options.dataKey || 'data'
    this.indexedDataKey = options.dataKey !== undefined
    this.version = options.version || 1
    this.indexList = options.indexList || []

    this.expireDuration = 30 * 24 * 60 * 60 * 1000 // default. 30일 후 만료.
    if (options.expireDuration != null) {
      this.expireDuration = options.expireDuration
    } else if (options.expireDuration === null) {
      this.expireDuration = null
    }

    this.expireDurationForEmpty = 3 * 60 * 1000 // default. 빈 데이터는 3분 후 만료.
    if (options.expireDurationForEmpty != null) {
      this.expireDurationForEmpty = options.expireDurationForEmpty
    } else if (options.expireDurationForEmpty === null) {
      this.expireDurationForEmpty = null
    }

    this.skipPreload = false
    if (options.skipPreload === true) {
      this.skipPreload = true
    }

    this.dataMap = {}
    this.isInitialing = false
    this.initWaitingReq = []
    this.isloading = false
    this.loadWaitingReq = []
    this.versionOutdated = false

    if (this.autoInit === true) {
      this.init()
    }
  }

  private applyExpireFunc = (item: DBItem): DBItem & { isExpired: () => boolean } => {
    const result = item as DBItem & { isExpired: () => boolean }
    if (item.expireTime != null) {
      result.isExpired = () => {
        return item.expireTime! < Date.now()
      }
    } else {
      result.isExpired = () => {
        return false
      }
    }
    return result
  }

  private init = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (this.isInitialing !== true) {
        this.isInitialing = true

        const DBOpenRequest = window.indexedDB.open(this.dbName, this.version)

        DBOpenRequest.onerror = (error) => {
          console.log('error - db open failed', error)
          this.isActive = false

          this.initWaitingReq.forEach((promise) => {
            promise.reject(error)
          })
          this.initWaitingReq = []

          this.isInitialing = false
          reject(error)
        }

        DBOpenRequest.onsuccess = (event) => {
          console.log('success - db open success.')

          this.db = (event.target as IDBOpenDBRequest).result

          // 다른 탭이 새 버전으로 upgrade를 요청하면 이 연결을 닫아야 해당 탭의 open이 block되지 않는다.
          this.db.onversionchange = () => {
            console.warn('warning - db version changed by another tab.', this.dbName)
            this.db?.close()
            this.db = null
            this.isActive = false
            this.loaded = false
            this.versionOutdated = true
            versionChangedPopup()
          }

          const onLoadSuccess = (ret: boolean): void => {
            this.initWaitingReq.forEach((promise) => {
              promise.resolve(ret)
            })
            this.initWaitingReq = []

            this.isInitialing = false
            resolve(ret)
          }

          const onLoadFail = (err: unknown): void => {
            this.initWaitingReq.forEach((promise) => {
              promise.reject(err)
            })
            this.initWaitingReq = []

            this.isInitialing = false
            reject(err)
          }

          this.load()
            .then((ret) => {
              this.isActive = true

              if (this.skipPreload === false) {
                this.getAllData()
                  .then(() => {
                    onLoadSuccess(ret)
                  })
                  .catch((err1) => {
                    onLoadFail(err1)
                  })
              } else {
                onLoadSuccess(ret)
              }
            })
            .catch((err) => {
              onLoadFail(err)
            })
        }

        DBOpenRequest.onblocked = (event) => {
          console.warn('warning - db blocked.', event)
          blockedPopup()
        }

        DBOpenRequest.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result

          db.onerror = (error) => {
            console.log('error - db upgrade.', error)
            this.isActive = false

            this.initWaitingReq.forEach((promise) => {
              promise.reject(error)
            })
            this.initWaitingReq = []

            this.isInitialing = false
            reject(error)
          }

          const evt = event as IDBVersionChangeEvent
          if (evt.oldVersion != null && evt.oldVersion > 0) {
            // Some browsers keep the handler even if the store was already removed; guard to avoid NotFoundError.
            try {
              if (db.objectStoreNames.contains(this.dbName)) {
                db.deleteObjectStore(this.dbName)
              }
            } catch (err) {
              console.warn('Failed to delete old object store (ignored).', err)
            }
          }

          const objectStore = db.createObjectStore(this.dbName, { keyPath: this.key })

          if (this.expireDuration != null) {
            objectStore.createIndex('expireTime', 'expireTime', { unique: false })
          }

          if (this.indexedDataKey === true) {
            objectStore.createIndex(this.dataKey, this.dataKey, { unique: false })
          }

          this.indexList.forEach((item) => {
            objectStore.createIndex(item, item, { unique: false })
          })
        }
      } else {
        this.initWaitingReq.push({
          resolve,
          reject,
        })
      }
    })
  }

  close = (): void => {
    if (this.isActive === true && this.db) {
      this.db.close()
    }
    this.isActive = false
  }

  private load = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      if (this.isloading !== true) {
        this.dataMap = {}
        this.isloading = true
        if (this.expireDuration != null && this.db) {
          const transaction = this.db.transaction([this.dbName], 'readwrite')

          transaction.onerror = (tEvent) => {
            console.log('Transaction not opened due to error.', tEvent)
            reject(tEvent)
          }

          const objectStore = transaction.objectStore(this.dbName)
          const index = objectStore.index('expireTime')

          const keyRangeValue = IDBKeyRange.upperBound(Date.now())
          const req = index.openCursor(keyRangeValue)
          req.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null

            // 만료된 데이터 제거.
            if (cursor) {
              cursor.delete()
              cursor.continue()
            } else {
              this.loaded = true

              this.loadWaitingReq.forEach((promise) => {
                promise.resolve(true)
              })
              this.loadWaitingReq = []

              this.isloading = false

              resolve(true)
            }
          }

          req.onerror = (err) => {
            console.log('cursor read error.', err)
            this.loadWaitingReq.forEach((promise) => {
              promise.reject(err)
            })
            this.loadWaitingReq = []

            this.isloading = false

            reject(err)
          }
        } else {
          this.loaded = true
          this.isloading = false
          resolve(true)
        }
      } else {
        this.loadWaitingReq.push({
          resolve,
          reject,
        })
      }
    })
  }

  addData = (key: string, data: any, expireDuration?: number): Promise<T | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<T>((resolve, reject) => {
      const transaction = this.db!.transaction([this.dbName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(this.dbName)

      const newItem: any = {
        [this.key]: key,
        [this.dataKey]: data,
      }

      this.indexList.forEach((item) => {
        newItem[item] = data[item]
      })

      if (expireDuration != null) {
        newItem.expireTime = Date.now() + expireDuration
      } else if (this.expireDuration != null) {
        newItem.expireTime = Date.now() + this.expireDuration
      }

      const objectStoreRequest = objectStore.add(newItem)

      objectStoreRequest.onsuccess = () => {
        let item: any = this.applyExpireFunc(newItem)

        if (this.options.getSaveData != null) {
          item = this.options.getSaveData(newItem)
        }

        this.dataMap[key] = item

        resolve(this.dataMap[key])
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  putData = (key: string, data: any, expireDuration?: number): Promise<T | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<T>((resolve, reject) => {
      const transaction = this.db!.transaction([this.dbName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(this.dbName)

      const newItem: any = {
        [this.key]: key,
        [this.dataKey]: data,
      }

      this.indexList.forEach((item) => {
        newItem[item] = data[item]
      })

      if (expireDuration != null) {
        newItem.expireTime = Date.now() + expireDuration
      } else if (this.expireDuration != null) {
        newItem.expireTime = Date.now() + this.expireDuration
      }

      const objectStoreRequest = objectStore.put(newItem)

      objectStoreRequest.onsuccess = () => {
        if (this.options.beforeRemoveSaveData != null && this.dataMap[key]) {
          this.options.beforeRemoveSaveData(this.dataMap[key])
        }

        let item: any = this.applyExpireFunc(newItem)

        if (this.options.getSaveData != null) {
          item = this.options.getSaveData(newItem)
        }

        this.dataMap[key] = item

        resolve(this.dataMap[key])
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  deleteData = (key: string): Promise<string | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<string>((resolve, reject) => {
      if (this.dataMap[key] != null) {
        if (this.options.beforeRemoveSaveData != null) {
          this.options.beforeRemoveSaveData(this.dataMap[key])
        }

        delete this.dataMap[key]
      }

      const transaction = this.db!.transaction([this.dbName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(this.dbName)

      const objectStoreRequest = objectStore.delete(key)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Deleted successfully.', event)
        resolve(key)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to delete.', error)
        reject(error)
      }
    })

    return promise
  }

  clear = (): Promise<boolean | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (this.options.beforeRemoveSaveData != null) {
        Object.values(this.dataMap).forEach((value) => {
          this.options.beforeRemoveSaveData!(value)
        })
      }

      this.dataMap = {}

      const transaction = this.db!.transaction([this.dbName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(this.dbName)

      const objectStoreRequest = objectStore.clear()

      objectStoreRequest.onsuccess = (event) => {
        console.log('cleared successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to clear.', error)
        reject(error)
      }
    })

    return promise
  }

  getData = (key: string): Promise<T | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<T>((resolve, reject) => {
      const cached: any = this.dataMap[key]
      if (cached != null && (cached.isExpired == null || cached.isExpired() === false)) {
        if (this.dataKey in cached) {
          resolve(cached)
        } else {
          resolve(null)
        }
      } else {
        const transaction = this.db!.transaction([this.dbName], 'readonly')

        transaction.onerror = (error) => {
          console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
          reject(error)
        }

        const objectStore = transaction.objectStore(this.dbName)

        const objectStoreRequest = objectStore.get(key)

        objectStoreRequest.onsuccess = () => {
          const { result } = objectStoreRequest

          if (result != null) {
            let item: any = this.applyExpireFunc({ ...result })

            if (this.options.getSaveData != null) {
              item = this.options.getSaveData(item)
            }

            this.dataMap[key] = item

            resolve(this.dataMap[key])
          } else {
            console.log('No matching data.', this.dbName, key)
            const item: any = this.applyExpireFunc({
              [this.key]: key,
              expireTime:
                this.expireDurationForEmpty != null
                  ? Date.now() + this.expireDurationForEmpty
                  : null,
            })
            this.dataMap[key] = item
            resolve(null)
          }
        }

        objectStoreRequest.onerror = (error) => {
          console.log('failed to get.', error)
          reject(error)
        }
      }
    })

    return promise
  }

  getAllData = (): Promise<{ dataMap: Record<string, T>; dataList: T[] } | null> => {
    if (this.isActive !== true || !this.db) {
      return Promise.resolve(null)
    }

    const promise = new Promise<{ dataMap: Record<string, T>; dataList: T[] }>(
      (resolve, reject) => {
        const transaction = this.db!.transaction([this.dbName], 'readonly')

        transaction.onerror = (error) => {
          console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
          reject(error)
        }

        const objectStore = transaction.objectStore(this.dbName)

        const cursorRequest = objectStore.openCursor()

        const dataList: T[] = []
        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null
          if (cursor) {
            let item: any = this.applyExpireFunc({ ...cursor.value })
            if (this.options.getSaveData != null) {
              item = this.options.getSaveData(item, cursor.key)
            }

            this.dataMap[cursor.key as string] = item
            if (
              this.options.checkForList == null ||
              this.options.checkForList(item, cursor.key) === true
            ) {
              dataList.push(item)
            }

            cursor.continue()
          } else {
            resolve({
              dataMap: this.dataMap,
              dataList,
            })
          }
        }

        cursorRequest.onerror = (error) => {
          console.log('failed to getAll.', error)
          reject(error)
        }
      },
    )

    return promise
  }

  isLoaded = (): boolean => {
    return this.loaded
  }

  getDB = async (): Promise<boolean> => {
    if (this.isActive === true) {
      return Promise.resolve(true)
    }
    // 다른 탭에서 버전이 올라간 뒤에는 현재(구버전) 코드로 다시 열면 VersionError가 발생한다.
    if (this.versionOutdated === true) {
      return Promise.resolve(false)
    }
    const initRes = await this.init()
    return initRes
  }
}

export default BaseDB

export function blockedPopup(): void {
  setTimeout(() => {
    const confirm = window.confirm(i18n.t('db.blocked'))
    if (confirm === true) {
      const channel = new BroadcastChannel('appMessageChannel')
      const sessionId = sessionStorage.getItem('app.session.id')
      const data = {
        cmd: 'reload',
        senderId: sessionId,
      }
      channel.postMessage(JSON.stringify(data))
    }
  }, 1000)
}

// 여러 DB 인스턴스에서 versionchange가 동시에 발생해도 confirm은 한 번만 표시한다.
let versionChangedPromptShown = false

export function versionChangedPopup(): void {
  if (versionChangedPromptShown === true) {
    return
  }
  versionChangedPromptShown = true

  const confirm = window.confirm(i18n.t('db.versionChanged'))
  if (confirm === true) {
    window.location.reload()
  }
}
